import React, { useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { SATLinkStatus } from '../../@types/SATLinking';
import { SubscriptionStatus } from '../../@types/Subscription';
import UserCredentialsLinked from './organisms/UserCredentialsLinked';
import growthbookFile from '../../shared/constants/growthbook';
import { ANALYTICS_EVENTS } from '../../shared/constants/events';
import { RootState } from '../../redux/store';
import useCleverTapEvent from '../../hooks/useClevertapEvent';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import GROWTHBOOK_FEATURE_FLAGS from '../../shared/utils/growthbook.util';
import usePageView from '../../hooks/usePageView';
import { EVENT_TYPES } from '../../shared/constants/events';
import { useService } from '../../hooks/useService';

import UnlinkedUser from './organisms/UnlinkedUser';
import SatAlert from './molecules/SatAlert';
import FailedRenewalBanner from './molecules/FailedRenewalBanner';
import { PRODUCTS_SLUG } from '../../shared/constants/SlugsProducts';
import UserZero from './organisms/UserZero';

const HomePage: React.FC = () => {
	const logClevertapEvent = useCleverTapEvent();
	const { status, isLoading: isLoadingSatStatus } = useSelector(
		(state: RootState) => state.satLinkStatus,
	);
	const { user: userProfile, activeSuscriptions } = useSelector(
		(state: RootState) => state.profile,
	);
	const { trackPageView } = usePageView({
		sectionVisit: EVENT_TYPES.PAGE_VIEW_HOME,
		isHomePage: true,
	});
	const {
		getSatLinkedAccount,
		getAcquisitions,
		getOnboardingIAProgress,
		getSubscriptions,
		isLoading,
	} = useService();

	// Check if there's a failed renewal subscription
	const hasFailedRenewal = activeSuscriptions.some(
		(subscription) =>
			subscription.payment_status === SubscriptionStatus.failed &&
			subscription.active === true &&
			subscription.renew_at_period_end === true,
	);

	// Get the failed subscription for the modal
	const failedSubscription = activeSuscriptions.find(
		(subscription) =>
			subscription.payment_status === SubscriptionStatus.failed &&
			subscription.active === true &&
			subscription.renew_at_period_end === true,
	);

	const showErrorSatAlert = useFeatureIsOn(
		GROWTHBOOK_FEATURE_FLAGS.SHOW_ERROR_SAT_ALERT,
	);

	const { acquisitions } = useSelector((state: RootState) => state.productList);

	const acquisitionsWithoutAsat = acquisitions.filter(
		(acquisition) =>
			acquisition?.product?.slug !== PRODUCTS_SLUG.ASSISTANCE_ASAT,
	);

	const userIsLinked = status?.status === SATLinkStatus.ACTIVE;
	const userHavePurchases =
		acquisitionsWithoutAsat.length > 0 || activeSuscriptions.length > 0;

	const showUnlinkedUser = userHavePurchases && !userIsLinked;
	const showUserZero = !userHavePurchases && !userIsLinked;

	const isLoadingPage =
		isLoadingSatStatus ||
		isLoading.adquisitions ||
		isLoading.onboardingIAProgress ||
		isLoading.serviceStatus;

	const loadGrowthbook = async () => {
		await growthbookFile.loadFeatures();
	};

	useEffect(() => {
		if (userProfile?.id) {
			loadGrowthbook();
			growthbookFile?.setAttributes({
				user_id: userProfile?.id,
			});
			logClevertapEvent(ANALYTICS_EVENTS.PAGE_VIEWED, {
				name: '/home',
			});
			if (window.clarity) {
				window.clarity('identify', userProfile.id.toString());
			}

			getOnboardingIAProgress(userProfile.id);
			getSatLinkedAccount(userProfile.id, userProfile.cellphone);
		}
	}, [userProfile?.id]);

	useEffect(() => {
		if (!isLoadingSatStatus) {
			trackPageView();
		}
	}, [isLoadingSatStatus]);

	useEffect(() => {
		getAcquisitions();
		getSubscriptions();
	}, []);

	const handleRetryPayment = () => {
		// This function is now a fallback, the modal will handle the main logic
	};

	return (
		<section className='p-3 md:py-7'>
			{isLoadingPage ? (
				<div className='flex items-center justify-center mt-5'>
					<CircularProgress size={25} />
				</div>
			) : (
				<>
					{hasFailedRenewal && (
						<FailedRenewalBanner
							onRetry={handleRetryPayment}
							failedSubscription={failedSubscription}
						/>
					)}
					{showErrorSatAlert && <SatAlert />}
					{showUserZero ? (
						<UserZero />
					) : showUnlinkedUser ? (
						<UnlinkedUser />
					) : (
						<UserCredentialsLinked />
					)}
				</>
			)}
			<div className='h-[120px]' />
		</section>
	);
};

export default React.memo(HomePage);
